import React, { useEffect, useState } from "react";
import { Wind, Flame, Leaf, Moon, Utensils, Zap, Circle, Droplet, Mountain } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { CtaButton } from "@/components/CtaButton";
import { SITE } from "@/constants/testIds";

const pickIcon = (name) => {
    const map = {
        wind: Wind,
        flame: Flame,
        leaf: Leaf,
        moon: Moon,
        utensils: Utensils,
        zap: Zap,
        circle: Circle,
        droplet: Droplet,
        mountain: Mountain,
    };
    const I = map[name] || Leaf;
    return <I className="h-6 w-6 text-moss" strokeWidth={1.5} />;
};

const doshaAccent = {
    vata: "bg-seaGlass/25 text-moss border-seaGlass/40",
    pitta: "bg-terracotta/15 text-terracotta border-terracotta/30",
    kapha: "bg-sage/20 text-moss border-sage/40",
};

export default function WhyAyurvedaPage() {
    const [d, setD] = useState({ pillars: [], elements: [], doshas: [] });
    useEffect(() => {
        Promise.all([api.pillars(), api.elements(), api.doshas()])
            .then(([pillars, elements, doshas]) => setD({ pillars, elements, doshas }))
            .catch(() => {});
    }, []);

    return (
        <div>
            <PageHeader
                eyebrow="WHY AYURVEDA WORKS"
                title="The mother of all healing sciences"
                subtitle="Ayurveda is a 5,000-year-old wholistic system from India that sees the soul, mind, senses, and body as one coordinated whole — and uses food, herbs, bodywork, and lifestyle to keep them in harmony."
                breadcrumbs={[{ label: "Home", to: "/" }, { label: "Why Ayurveda" }]}
            />

            {/* Three Pillars */}
            <section className="py-12 sm:py-16">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <p className="font-accent text-xs text-terracotta">THE THREE PILLARS</p>
                        <h2 className="mt-2 font-display text-3xl sm:text-4xl text-moss leading-tight">
                            Sleep • Digestion • Energy
                        </h2>
                        <p className="mt-3 text-foreground/75 leading-relaxed">
                            Strengthen any one of these and the others begin to rebalance themselves.
                        </p>
                    </div>
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-5">
                        {d.pillars.map((p) => (
                            <div
                                key={p.id}
                                data-testid={SITE.pillarCard}
                                className="rounded-2xl border border-border/80 bg-white/85 p-6"
                            >
                                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sand">
                                    {pickIcon(p.icon)}
                                </div>
                                <h3 className="mt-4 font-display text-xl text-moss">{p.name}</h3>
                                <p className="mt-2 text-sm text-foreground/75 leading-relaxed">{p.summary}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Five Elements */}
            <section className="py-12 sm:py-16 sand-wash bg-grain">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <p className="font-accent text-xs text-terracotta">THE FIVE ELEMENTS</p>
                        <h2 className="mt-2 font-display text-3xl sm:text-4xl text-moss leading-tight">
                            Pancha Mahabhutas
                        </h2>
                        <p className="mt-3 text-foreground/75 leading-relaxed">
                            Every human being is made of the same five elements that make the world.
                        </p>
                    </div>
                    <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                        {d.elements.map((e) => (
                            <div
                                key={e.id}
                                data-testid={SITE.elementCard}
                                className="rounded-2xl border border-border/80 bg-white/85 p-5"
                            >
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sand">
                                    {pickIcon(e.icon)}
                                </div>
                                <h3 className="mt-3 font-display text-lg text-moss">{e.name}</h3>
                                <p className="text-xs text-foreground/55">{e.sanskrit}</p>
                                <p className="mt-2 text-sm text-foreground/75 leading-relaxed">{e.summary}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Three Doshas */}
            <section className="py-12 sm:py-16">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <p className="font-accent text-xs text-terracotta">THE THREE DOSHAS</p>
                        <h2 className="mt-2 font-display text-3xl sm:text-4xl text-moss leading-tight">
                            Vata • Pitta • Kapha
                        </h2>
                        <p className="mt-3 text-foreground/75 leading-relaxed">
                            The three biological energies that govern every body and mind. Most of us are a unique blend of all three.
                        </p>
                    </div>
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {d.doshas.map((dosha) => (
                            <article
                                key={dosha.slug}
                                data-testid={SITE.doshaCard}
                                data-slug={dosha.slug}
                                className={`rounded-3xl border-2 p-6 ${doshaAccent[dosha.slug] || "border-border/80 bg-white/85"}`}
                            >
                                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-cream/80 border border-border/60">
                                    {pickIcon(dosha.icon)}
                                </div>
                                <h3 className="mt-4 font-display text-2xl text-moss">{dosha.name}</h3>
                                <p className="text-xs text-foreground/60">{dosha.elements}</p>
                                <p className="mt-3 text-sm text-foreground/85 leading-relaxed">{dosha.summary}</p>

                                <div className="mt-4 flex flex-wrap gap-1.5">
                                    {dosha.qualities.map((q) => (
                                        <span
                                            key={q}
                                            className="rounded-full bg-cream/80 border border-border/60 px-2 py-0.5 text-[0.7rem] text-foreground/70"
                                        >
                                            {q}
                                        </span>
                                    ))}
                                </div>

                                <div className="mt-5 space-y-3 text-sm">
                                    <div>
                                        <p className="font-accent text-[0.7rem] text-terracotta">IN BALANCE</p>
                                        <p className="mt-1 text-foreground/80">{dosha.in_balance}</p>
                                    </div>
                                    <div>
                                        <p className="font-accent text-[0.7rem] text-terracotta">OUT OF BALANCE</p>
                                        <p className="mt-1 text-foreground/80">{dosha.out_of_balance}</p>
                                    </div>
                                    <div>
                                        <p className="font-accent text-[0.7rem] text-terracotta">BALANCE WITH</p>
                                        <ul className="mt-1 list-disc pl-4 text-foreground/80 space-y-0.5">
                                            {dosha.balance_with.map((b) => (
                                                <li key={`${dosha.slug}-${b}`}>{b}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>

                    <div className="mt-10 rounded-3xl bg-moss text-cream p-8 sm:p-10 text-center">
                        <h3 className="font-display text-2xl sm:text-3xl">
                            Curious which dosha rules your day?
                        </h3>
                        <p className="mt-2 text-cream/85 max-w-xl mx-auto">
                            Riley will map your prakruti (constitution) and vikruti (current imbalance) in a 2-hour intake — then build a daily life around your truth.
                        </p>
                        <div className="mt-5 inline-flex">
                            <CtaButton to="/contact" variant="accent">
                                Book your consultation
                            </CtaButton>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
